import { useState, useRef, useEffect } from 'react'
import {
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Card,
  Animate,
  useToast
} from 'wss3-forge'
import {
  Alert20Regular,
  Checkmark20Regular,
  ShieldCheckmark20Regular,
  Key20Regular,
  Location20Regular,
  Password20Regular,
  PersonDelete20Regular,
  Dismiss16Regular
} from '@fluentui/react-icons'
import { useSecurity } from '../hooks/useSecurity'
import type { SecurityNotification } from '../types/database'

function formatTimeAgo(date: string): string {
  const d = new Date(date)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const minutes = Math.floor(diff / (1000 * 60))
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString()
}

function getNotificationIcon(type: string) {
  switch (type) {
  case 'new_device_login':
    return <Location20Regular />
  case 'password_changed':
    return <Password20Regular />
  case 'passkey_added':
  case 'passkey_removed':
    return <Key20Regular />
  case 'account_deletion_scheduled':
  case 'account_deletion_cancelled':
    return <PersonDelete20Regular />
  case 'suspicious_activity':
    return <Alert20Regular style={{ color: 'var(--color-error)' }} />
  default:
    return <ShieldCheckmark20Regular />
  }
}

export function NotificationsDropdown() {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useSecurity()

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleMarkAsRead = async (notification: SecurityNotification) => {
    if (notification.read) return
    const { error } = await markAsRead(notification.id)
    if (error) {
      toast.error('Failed to mark as read')
    }
  }

  const handleMarkAllAsRead = async () => {
    const { error } = await markAllAsRead()
    if (error) {
      toast.error('Failed to mark all as read')
    } else {
      toast.success('All notifications marked as read')
    }
  }

  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation()
    const { error } = await deleteNotification(notificationId)
    if (error) {
      toast.error('Failed to delete notification')
    }
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          position: 'relative',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Alert20Regular />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              backgroundColor: 'var(--color-error)',
              color: 'white',
              fontSize: '10px',
              fontWeight: 600,
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <Animate type="fadeIn">
          <Card
            padding="none"
            style={{
              position: 'absolute',
              top: '48px',
              right: 0,
              width: '360px',
              maxHeight: '480px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }}
          >
            <VStack gap="none">
              <HStack
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 1rem'
                }}
              >
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Text weight="semibold">Notifications</Text>
                  {unreadCount > 0 && (
                    <Badge variant="primary" size="sm">{unreadCount}</Badge>
                  )}
                </HStack>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Checkmark20Regular />}
                    onClick={handleMarkAllAsRead}
                  >
                    Mark all read
                  </Button>
                )}
              </HStack>

              {notifications.length === 0 ? (
                <VStack gap="sm" style={{ padding: '2rem', alignItems: 'center' }}>
                  <Alert20Regular style={{ width: 32, height: 32, color: 'var(--text-muted)' }} />
                  <Text color="muted" size="sm">No notifications</Text>
                </VStack>
              ) : (
                <VStack gap="xs" style={{ padding: '0 0.5rem 0.5rem 0.5rem' }}>
                  {notifications.slice(0, 10).map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleMarkAsRead(notification)}
                      style={{
                        padding: '0.75rem',
                        cursor: notification.read ? 'default' : 'pointer',
                        backgroundColor: 'var(--bg-tertiary)',
                        borderRadius: '8px',
                        transition: 'background-color 0.15s ease'
                      }}
                    >
                      <HStack gap="sm" style={{ alignItems: 'flex-start' }}>
                        <div style={{ color: 'var(--brand-primary)', marginTop: '2px' }}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <VStack gap="xs" style={{ flex: 1 }}>
                          <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text size="sm" weight={notification.read ? 'normal' : 'medium'}>{notification.title}</Text>
                            <HStack gap="xs" style={{ alignItems: 'center' }}>
                              {!notification.read && (
                                <div
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--brand-primary)'
                                  }}
                                />
                              )}
                              <button
                                onClick={(e) => handleDelete(e, notification.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '2px',
                                  cursor: 'pointer',
                                  color: 'var(--text-muted)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  transition: 'color 0.15s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-error)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                              >
                                <Dismiss16Regular />
                              </button>
                            </HStack>
                          </HStack>
                          <Text size="xs" color="muted" style={{ lineHeight: 1.4 }}>
                            {notification.message}
                          </Text>
                          <Text size="xs" color="muted">
                            {formatTimeAgo(notification.created_at)}
                          </Text>
                        </VStack>
                      </HStack>
                    </div>
                  ))}
                </VStack>
              )}
            </VStack>
          </Card>
        </Animate>
      )}
    </div>
  )
}
